/*
 * Copyright (c) 2026. Bluemate B.V.
 */

import React, {useContext, useEffect, useState} from 'react';
import {Navigate, useSearchParams} from 'react-router';
import {useTranslation} from 'react-i18next';
import {AppContext} from '../misc/appContext';
import {LOGIN_ROUTE, MOBILE_APP_INSTALL_MENU, MOBILE_APP_INSTALL_PARAM, MOBILE_APP_ROUTE} from '../../util/constants';
import {onSetCrumbs} from '../../util/events';
import {isAndroid, isStandalone, PWA_INSTALL_AVAILABLE_EVENT} from '../../util/pwaUtil';
import {isPwaInstalledOnThisDevice} from '../../util/pwaInstallState';
import {useAndroidInstallBannerMode} from '../pwa/androidInstallBanner';
import MobileApp, {MobilePlatform} from './mobileApp';

export default function MobileAppController() {
    const {user} = useContext(AppContext).userContext;
    const {t} = useTranslation();
    const [searchParams] = useSearchParams();
    // Arrived from the "your browser is not offering the install button" banner:
    // that visitor needs the browser-menu route specifically, so open on Android
    // regardless of what the user agent claims (desktop-site mode rewrites it).
    const menuInstallRequested = searchParams.get(MOBILE_APP_INSTALL_PARAM) === MOBILE_APP_INSTALL_MENU;
    // Default to the visitor's own platform; iOS and desktop start on the Apple tab.
    const [platform, setPlatform] = useState<MobilePlatform>(menuInstallRequested || isAndroid() ? 'android' : 'apple');
    // Whether the app is ALREADY installed here — the install steps then become
    // noise, and the live-banner step in particular pointed at a banner that
    // hides itself once installed (empty step under "follow the banner below").
    const [installedOnDevice, setInstalledOnDevice] = useState(false);
    // The live banner decides for itself whether it has anything to render; the
    // step that frames it must follow that decision, not guess at it.
    const bannerMode = useAndroidInstallBannerMode();

    useEffect(() => {
        onSetCrumbs([{label: t('mobileApp.crumb', 'Mobiele app'), route: MOBILE_APP_ROUTE}]);
    }, []);

    useEffect(() => {
        let cancelled = false;
        const check = () =>
            void isPwaInstalledOnThisDevice().then(value => {
                if (!cancelled) setInstalledOnDevice(value);
            });
        check();
        // `beforeinstallprompt` firing is a definitive "not installed" signal
        // (and follows an uninstall), so re-check on it.
        window.addEventListener(PWA_INSTALL_AVAILABLE_EVENT, check);
        window.addEventListener('appinstalled', check);
        return () => {
            cancelled = true;
            window.removeEventListener(PWA_INSTALL_AVAILABLE_EVENT, check);
            window.removeEventListener('appinstalled', check);
        };
    }, []);

    if (!user) return <Navigate to={LOGIN_ROUTE} />;

    return (
        <MobileApp
            platform={platform}
            onSelectPlatform={setPlatform}
            showAndroidBanner={isAndroid() && !installedOnDevice && bannerMode !== null}
            runningAsApp={isStandalone()}
            focusMenuInstall={menuInstallRequested && !installedOnDevice}
        />
    );
}
